import React from 'react';
import '../../../styles/style.css';

const RouteDetails = ({ details = [], routeStats = null }) => {
  // Если нет данных, показываем загрузку
  if ((!details || details.length === 0) && !routeStats) {
    return (
      <div className="routeAdditionalDesc">
        <div className="routeAdditionalDescStick"></div>
        <p className="routeMetaInfo">
          Информация о маршруте загружается...
        </p>
      </div>
    );
  }

  // Сортировка массива по полю sort_order (как в LocationDetails)
  const sortedDetails = details && details.length > 0 
    ? [...details].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    : [];

  // Форматирование расстояния: добавление "км"
  const formatDistance = (distance) => {
    if (!distance && distance !== 0) return null;
    
    // Если это число, форматируем с км
    if (typeof distance === 'number') {
      // Форматируем число с заменой точки на запятую для красоты
      const formattedNumber = distance.toString().replace('.', ',');
      return `${formattedNumber} км`;
    }
    
    // Если это строка
    if (typeof distance === 'string') {
      // Если уже содержит "км", возвращаем как есть
      if (distance.toLowerCase().includes('км')) return distance;
      // Иначе добавляем "км"
      return `${distance} км`;
    }
    
    return distance;
  };

  // Форматирование времени: из минут в "Xч Yмин"
  const formatTime = (time) => {
    if (!time && time !== 0) return null;
    
    // Если это число (минуты)
    if (typeof time === 'number') {
      const hours = Math.floor(time / 60);
      const remainingMinutes = time % 60;
      
      let result = '';
      if (hours > 0) {
        result += `${hours}ч `;
      }
      if (remainingMinutes > 0) {
        result += `${remainingMinutes}мин`;
      }
      if (result === '') {
        result = '0мин';
      }
      
      return `${result} (пешком)`;
    }
    
    // Если это строка
    if (typeof time === 'string') {
      // Если уже содержит "ч" или "мин", возвращаем как есть (добавляя пешком если нужно)
      if (time.includes('ч') || time.includes('мин')) {
        return time.includes('пешком') ? time : `${time} (пешком)`;
      }
      
      // Пробуем преобразовать в число (минуты)
      const minutes = parseInt(time, 10);
      if (isNaN(minutes)) return time;
      
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      let result = '';
      if (hours > 0) {
        result += `${hours}ч`;
      }
      if (remainingMinutes > 0) {
        result += `${remainingMinutes}мин`;
      }
      if (result === '') {
        result = '0мин';
      }
      
      return `${result} (пешком)`;
    }
    
    return time;
  };

  // Склонение слова "шаг" / "шага" / "шагов"
  const formatSteps = (steps) => {
    if (!steps && steps !== 0) return null;
    
    // Если это число
    if (typeof steps === 'number') {
      const lastDigit = steps % 10;
      const lastTwoDigits = steps % 100;
      
      let word;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        word = 'шагов';
      } else if (lastDigit === 1) {
        word = 'шаг';
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        word = 'шага';
      } else {
        word = 'шагов';
      }
      
      return `${steps} ${word}`;
    }
    
    // Если это строка
    if (typeof steps === 'string') {
      // Если уже содержит текст, возвращаем как есть
      if (steps.includes('шаг') || steps.includes('шагов')) {
        return steps;
      }
      
      // Пробуем преобразовать в число
      const stepsNumber = parseInt(steps, 10);
      if (isNaN(stepsNumber)) return steps;
      
      const lastDigit = stepsNumber % 10;
      const lastTwoDigits = stepsNumber % 100;
      
      let word;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        word = 'шагов';
      } else if (lastDigit === 1) {
        word = 'шаг';
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        word = 'шага';
      } else {
        word = 'шагов';
      }
      
      return `${stepsNumber} ${word}`;
    }
    
    return steps;
  };

  return (
    <div className="routeAdditionalDesc">
      <div className="routeAdditionalDescStick"></div>
      
      {/* Динамические блоки информации (как в LocationDetails) */}
      {sortedDetails.map((detail, index) => {
        // Обработка описания: разбивка по \n или \\n
        const descriptionLines = detail.description
          ? detail.description.split(/\r?\n|\\n/).filter(line => line.trim() !== '')
          : [];

        return (
          <div key={index} className="workingHours">
            <b>{detail.title}</b>:
            
            {descriptionLines.length > 0 ? (
              <>
                {/* Проверяем, начинался ли текст с \n или \\n */}
                {(/^\\?n/.test(detail.description.trimStart()) || detail.description.startsWith('\n')) ? (
                  <>
                    {descriptionLines.map((line, i) => (
                      <p key={i}>
                        {line.trim()}
                      </p>
                    ))}
                  </>
                ) : (
                  <>
                    {' '}
                    {descriptionLines[0].trim()}
                    {descriptionLines.slice(1).map((line, i) => (
                      <p key={i}>
                        {line.trim()}
                      </p>
                    ))}
                  </>
                )}
              </>
            ) : (
              <span> Нет данных</span>
            )}
          </div>
        );
      })}

      {/* Блок статистики (расстояние, время, шаги) — как у локаций */}
      {routeStats && (routeStats.distance || routeStats.time || routeStats.steps) && (
        <div className="workingHours">
          <b>О маршруте</b>:
          
          <div className="routeStats" style={{ marginTop: '8px' }}>
            {routeStats.distance !== undefined && routeStats.distance !== null && (
              <div className="routeStatItem">
                <img src="/elements/roadIcon.svg" alt="Расстояние" className="routeStatIcon" />
                <span className="working-hours-value">{formatDistance(routeStats.distance)}</span>
              </div>
            )}
            {routeStats.time !== undefined && routeStats.time !== null && (
              <div className="routeStatItem">
                <img src="/elements/timeIcon.svg" alt="Время" className="routeStatIcon" />
                <span className="working-hours-value">{formatTime(routeStats.time)}</span>
              </div>
            )}
            {routeStats.steps !== undefined && routeStats.steps !== null && (
              <div className="routeStatItem">
                <img src="/elements/stepsIcon.svg" alt="Шаги" className="routeStatIcon steps-icon" />
                <span className="working-hours-value">{formatSteps(routeStats.steps)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetails;